import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Box, Button, Paper } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IRowNode,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";

import { mesfColors, hexToRgba } from "@/setup/ag-grid/mesf-theme";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import { useOperationalWritesLocked } from "@/shared/hooks/useOperationalWritesLocked";
import { useTableData, useDeleteDelay } from "../../hooks";
import { useDelaysOptionFunctions } from "../../hooks/useDelaysOptionFunctions";
import { DelayByShift } from "../../models/DelayByShift";
import { SplitDelaysModal } from "../modals/SplitDelaysModal";
import { UpsertJustifyDelayModal } from "../modals/UpsertJustifyDelayModal";

type Props = {
	delayId: string | null;
	action: "edit" | "justify" | "split" | null;
	isLoading: boolean;
	rows: DelayByShift[];
	refreshData: Function;
	assetId?: number;
	updateUrl: (newAction?: string | null, newDelayId?: string | null) => void;
	quickFilter?: string;
	onGridApiReady?: (api: GridApi) => void;
	onDialogOpenChange?: (open: boolean) => void;
};

export const DelaysManagerTable = ({
	delayId,
	action,
	isLoading,
	rows,
	refreshData,
	assetId,
	updateUrl,
	quickFilter = "",
	onGridApiReady,
	onDialogOpenChange,
}: Props) => {
	const writesLocked = useOperationalWritesLocked();
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	// Mutation hook for delete operation
	const { deleteDelay, isDeleting } = useDeleteDelay({
		onSuccess: () => {
			setNotificationMessage("Stop deleted successfully");
			setSelectedData(null);
		},
		onError: (err) => setError(err.message),
	});
	const [selectedData, setSelectedData] = useState<DelayByShift | null>(null);
	const [showModal, setShowModal] = useState<
		"new" | "edit" | "delete" | "justify" | "split" | ""
	>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const {
		createData,
		justifyData,
		splitData,
		getMenuOptions,
		messageDeleteModal,
		setMessageDeleteModal,
	} = useDelaysOptionFunctions({
		setSelectedData,
		setShowModal,
		updateUrl,
		writesLocked,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs } = useTableData({
		justifyData,
		splitData,
		showContextMenu,
	});
	const { defaultColDef } = useGridDefinitions({});

	const handleDeleteDelay = (value?: string) => {
		setShowModal("");
		setMessageDeleteModal("");
		updateUrl(null, null);
		if (value !== "OK") return;
		if (selectedData === null || selectedData.DelayId === null) return;
		deleteDelay(selectedData.DelayId);
	};

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		showContextMenu(event as any, data, "delayManagerContext");
		if (data) {
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	const onModalHide = (update: boolean) => {
		// Cache invalidation is handled by mutation hooks automatically
		setShowModal("");
		setSelectedData(null);
		updateUrl(null, null);
		if (gridApi) {
			gridApi.deselectAll();
		}
	};

	useEffect(() => {
		setShowModal(action ?? "");
	}, [action]);

	useEffect(() => {
		onDialogOpenChange?.(showModal !== "");
	}, [showModal]);

	useEffect(() => {
		if (!isLoading && gridApi) {
			if (delayId) {
				let foundNode = null;
				gridApi.forEachNode((node) => {
					if (node.data?.DelayId?.toString() === delayId) {
						foundNode = node;
					}
				});

				if (foundNode) {
					gridApi.ensureNodeVisible(foundNode);
					(foundNode as IRowNode).setSelected(true);
					setSelectedData((foundNode as IRowNode).data);
				}
			} else {
				setSelectedData(null);
			}
		}
	}, [isLoading, gridApi, delayId]);

	useEffect(() => {
		registerConfig({
			id: "delayManagerContext",
			getOptions: getMenuOptions,
		});
	}, [writesLocked]);

	useEffect(() => {
		if (gridApi) {
			gridApi.setGridOption("quickFilterText", quickFilter);
		}
	}, [gridApi, quickFilter]);

	return (
		<div
			onContextMenu={(e) => e.preventDefault()}
			style={{ height: "100%", display: "flex", flexDirection: "column" }}
		>
			<Paper elevation={2} sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<DelayByShift>
					loading={isLoading}
					onGridReady={(params) => {
						setGridApi(params.api);
						onGridApiReady?.(params.api);
					}}
					pagination={false}
					animateRows
					rowHeight={38}
					headerHeight={35}
					tooltipShowDelay={0}
					tooltipHideDelay={2000}
					rowData={rows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					getContextMenuItems={getContextMenuItems}
					getRowStyle={(params) => {
						if (params.data?.End === null) {
							return {
								background: hexToRgba(mesfColors.warning.main, 0.2),
								borderLeft: `4px solid ${mesfColors.warning.dark}`,
								fontWeight: "bold",
							};
						}
						return undefined;
					}}
					onRowClicked={(e) => {
						// Ignore clicks on the actions column (buttons, icons)
						const target = e.event?.target as HTMLElement;
						if (
							target?.closest("button") ||
							target?.closest("svg") ||
							target?.closest(".MuiIconButton-root")
						) {
							return;
						}
						// Single click opens the justify/edit modal
						updateUrl("justify", e.data?.DelayId?.toString());
						justifyData(e.data!);
					}}
					getRowId={(params) => `${params.data.DelayId}`}
					loadingOverlayComponent={CenteredLazyLoading}
					gridOptions={{
						rowSelection: {
							mode: "singleRow",
							checkboxes: false,
							enableClickSelection: true,
						},
					}}
					onCellKeyDown={(event) => {
						if ((event.event as KeyboardEvent).key === "Escape") {
							event.api.hidePopupMenu();
						}
					}}
				/>
			</Paper>
			<Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
				<Box sx={{ width: { xs: "100%", md: "calc(100% / 6)" } }}>
					<WriteLockTooltip locked={writesLocked} fullWidth>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							onClick={() => {
								createData();
							}}
							disabled={writesLocked}
						>
							Add Delay
						</Button>
					</WriteLockTooltip>
				</Box>
			</Box>
			<ConfirmationDialogRaw
				title="Confirm Delete"
				message={messageDeleteModal}
				open={showModal === "delete"}
				onClose={handleDeleteDelay}
				suffixTitle="Stops Manager"
			/>
			<UpsertJustifyDelayModal
				showModal={showModal}
				onHide={onModalHide}
				delayId={selectedData?.DelayId ?? (delayId ? Number(delayId) : null)}
				assetId={assetId}
				suffixTitle="Stops Manager"
				onSplit={(id) => {
					setShowModal("split");
					updateUrl("split", id.toString());
				}}
			/>
			<SplitDelaysModal
				show={showModal === "split"}
				onHide={onModalHide}
				delayId={selectedData?.DelayId ?? (delayId ? Number(delayId) : null)}
				assetId={assetId}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</div>
	);
};
